import React, { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useVirtualizer } from "@tanstack/react-virtual";

const LogRow = React.memo(
  ({
    virtualItem,
    relevantLogs,
    formatTimestamp,
    relativeTime,
    copyToClipboard,
  }) => {
    const log = relevantLogs[virtualItem.index];

    if (!log) return null;

    return (
      <div
        key={virtualItem.key}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: `${virtualItem.size}px`,
          transform: `translateY(${virtualItem.start}px)`,
        }}
        className="grid grid-cols-[40px_minmax(140px,1.4fr)_minmax(135px,1.3fr)_75px_70px_115px] items-center gap-2 px-4 py-3 text-sm border-b border-stone-100"
      >
        <div className="text-stone-600">{virtualItem.index + 1}</div>

        <div className="text-stone-700 whitespace-nowrap">
          {formatTimestamp(log.timestamp)}
        </div>

        {/* Display the controller identifier for the current log. */}
        <div className="text-stone-700 whitespace-nowrap">
          {log.controllerId ?? "-"}
        </div>

        {/* Display the door number associated with the log. */}
        <div className="text-stone-700 whitespace-nowrap">
          {log.byteParam2 ? `Door ${log.byteParam2}` : "-"}
        </div>

        <div className="text-stone-500 whitespace-nowrap">
          {relativeTime(log.timestamp)}
        </div>

        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            onClick={() => copyToClipboard(log.timestamp ?? "")}
            title="Copy ISO timestamp"
            className="rounded-md border border-stone-200 px-2 py-1 text-xs font-medium hover:bg-stone-50"
          >
            Copy
          </button>

          <a
            href={`data:text/plain;charset=utf-8,${encodeURIComponent(
              JSON.stringify(log, null, 2),
            )}`}
            download={`log-${virtualItem.index + 1}.json`}
            className="rounded-md border border-stone-200 px-2 py-1 text-xs font-medium hover:bg-stone-50"
          >
            JSON
          </a>
        </div>
      </div>
    );
  },
);

export default function LogDisplay({
  logs = [],
  title = "Logs",
  logType = "",
}) {
  const [query, setQuery] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);

  const parentRef = useRef(null);

  // Date formatting options reused by formatTimestamp.
  const dateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  // Format a valid timestamp for display.
  function formatTimestamp(isoString) {
    if (!isoString) return "-";

    const date = new Date(isoString);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-GB", dateTimeFormatOptions);
  }

  // Format a timestamp as a relative time.
  function relativeTime(isoString) {
    if (!isoString) return "";

    const timestamp = new Date(isoString).getTime();

    if (Number.isNaN(timestamp)) {
      return "";
    }

    const diffInSeconds = Math.floor((Date.now() - timestamp) / 1000);
    const isFuture = diffInSeconds < 0;
    const seconds = Math.abs(diffInSeconds);

    let value;
    let unit;

    if (seconds < 60) {
      value = seconds;
      unit = "s";
    } else if (seconds < 3600) {
      value = Math.floor(seconds / 60);
      unit = "m";
    } else if (seconds < 86400) {
      value = Math.floor(seconds / 3600);
      unit = "h";
    } else {
      value = Math.floor(seconds / 86400);
      unit = "d";
    }

    return isFuture ? `in ${value}${unit}` : `${value}${unit} ago`;
  }

  // Filter and sort logs based on the current search and sort options.
  const relevantLogs = useMemo(() => {
    const searchQuery = query.trim().toLowerCase();

    const base = logType
      ? logs.filter(
          (log) => !log.logDescription || log.logDescription === logType,
        )
      : logs;

    const filtered = searchQuery
      ? base.filter((log) => {
          const timestamp = (log.timestamp ?? "").toLowerCase();
          const formattedTimestamp = formatTimestamp(
            log.timestamp,
          ).toLowerCase();

          return (
            timestamp.includes(searchQuery) ||
            formattedTimestamp.includes(searchQuery) ||
            (log.extra &&
              JSON.stringify(log.extra).toLowerCase().includes(searchQuery))
          );
        })
      : base;

    return [...filtered].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();

      return newestFirst ? timeB - timeA : timeA - timeB;
    });
  }, [logs, logType, query, newestFirst]);

  const rowVirtualizer = useVirtualizer({
    count: relevantLogs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 5,
  });

  // Copy text to the system clipboard.
  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  }

  // Export the currently displayed logs as a CSV file.
  function exportCSV() {
    const header = ["Index", "Timestamp (ISO)", "Formatted", "Relative"];
    const rows = [header];

    relevantLogs.forEach((log, index) => {
      rows.push([
        index + 1,
        log.timestamp ?? "",
        formatTimestamp(log.timestamp),
        relativeTime(log.timestamp),
      ]);
    });

    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_").toLowerCase()}_${
      logType || "all"
    }.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-stone-50 border border-stone-200 px-4 py-3 shadow-sm">
            <div className="text-xs text-stone-500">{title} Count</div>

            <div className="mt-1 text-2xl font-extrabold text-stone-900 text-center">
              {relevantLogs.length}
            </div>
          </div>

          <div className="flex flex-col">
            <div className="text-sm font-medium text-stone-700">
              {logType || "All types"}
            </div>

            <div className="text-xs text-stone-500">
              Showing {relevantLogs.length} matching entries
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center md:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              id="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search timestamp / details..."
              className="h-11 w-full rounded-xl border border-stone-300 bg-white px-4 text-sm text-stone-700 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-200"
            />

            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-1 py-1 text-stone-500 hover:text-stone-700"
              >
                ✕
              </button>
            )}
          </div>

          <button
            onClick={() => setNewestFirst((state) => !state)}
            className="rounded-md border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
            aria-pressed={newestFirst}
            title={newestFirst ? "Sort: newest first" : "Sort: oldest first"}
          >
            {newestFirst ? "Newest" : "Oldest"}
          </button>

          <button
            onClick={exportCSV}
            className="rounded-md border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-md shadow-sm overflow-hidden">
        <div>
          <div>
            <div className="grid grid-cols-[40px_minmax(140px,1.4fr)_minmax(135px,1.3fr)_75px_70px_115px] items-center gap-2 px-4 py-3 text-sm border-b border-stone-100">
              <div>#</div>
              <div>Time of Event</div>
              <div>Device</div>
              <div>Door No.</div>
              <div>When</div>
              <div>Actions</div>
            </div>

            <div
              ref={parentRef}
              className="h-96 overflow-y-scroll overflow-x-hidden"
              style={{ scrollbarGutter: "stable" }}
            >
              {relevantLogs.length > 0 ? (
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                    <LogRow
                      key={virtualItem.key}
                      virtualItem={virtualItem}
                      relevantLogs={relevantLogs}
                      formatTimestamp={formatTimestamp}
                      relativeTime={relativeTime}
                      copyToClipboard={copyToClipboard}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-4 items-center px-4 py-3 text-sm border-b border-stone-100">
                  No logs found.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-stone-100 bg-stone-50 flex items-center justify-between text-xs text-stone-500">
          <div>{relevantLogs.length} entries</div>

          <div>
            Last updated:{" "}
            {relevantLogs[0] ? formatTimestamp(relevantLogs[0].timestamp) : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

LogDisplay.propTypes = {
  logs: PropTypes.array,
  title: PropTypes.string,
  logType: PropTypes.string,
};
